
import { addLineItems, checkout, clearCart, getDonationCategory } from '@/cart/cart-store';
import Modal from '@/components/Modal';
import ComponentToggleSignal from '@/components/component-toggle-signal';
import CarouselImagesSignal from '@/otp-gifts/carousel-images-signal';
import { GiftState, OtpSettings, OtpSettingsArr } from '@/otp-gifts/types';
import { getProductSelection, selectOption } from '@/product/product-selection';
import { VariantState } from '@/product/types';
import { SteppedProductSelectorSettings } from '@/settings/types';
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
 
const FragranceFree = "Fragrance Free"
const FreshScent = "Fresh Scent"

const scentToColor: Record<string, string> = {
    [FragranceFree]: 'btn-deep-ocean-blue',
    [FreshScent]: 'btn-fresh-scent' 
}

const scentToActiveColor: Record<string, string> = {
    [FragranceFree]: 'bg-deep-ocean-blue-hover/20',
    [FreshScent]: 'bg-fresh-scent-hover/20' 
}

const lockImg = "https://cdn.shopify.com/s/files/1/0665/0196/8109/files/lock.png?v=1720746351"

export default ProductSelectorOneStep;

const carouselImagesSignal = new CarouselImagesSignal()

/**
 * UPDATE THIS
 * Renders a one step buystack based on replo design.
 *
 * @param settings - The settings for the stepped product selector.
 * @returns The rendered component.
 */
function ProductSelectorOneStep({
    settings,
    otpSettingsArr
}: SteppedProductSelectorProps) {
    const {
        productOptionsSettingsMap,
     } = settings;

    const {product} = getProductSelection();

    const option = product?.options[0];
    const optionName = option?.name ?? '';

    const [chosenScent, setChosenScent] = useState(option?.values[0] ?? "")
    const [chosenLoad, setChosenLoad] = useState<VariantState>()
    const [currentScentVariants, setCurrentScentVariants] = useState<VariantState[]>([])
    const [variantToSettings, setVariantToSettings] = useState<Record<string, OtpSettings>>({})
    const [currentGifts, setCurrentGifts] = useState<GiftState[]>([])
    const [giftInModal, setGiftInModal] = useState<GiftState>()
    const modalController = useMemo(() => new ComponentToggleSignal(), []);

    const optionSettings = new Map(
        productOptionsSettingsMap
          ?.filter((setting) => setting.name === optionName)
          .map((setting) => [setting.value, setting]) ?? []
    );

    const getGiftCodes = (currentSettings: OtpSettings) => {
        const discountCodes: Record<string, boolean> = {}
        discountCodes[currentSettings.discount_code] = true
        currentGifts.map((g) => discountCodes[g.discount_code] = true)
        const discountCodesArr = Object.keys(discountCodes).filter(k => k)

        return discountCodesArr.join(',')
    }
    const addToCart = async () => {
        if (!chosenLoad) {
            throw new Error('No variant selected');
        }


        const chosenNumberOfLoads = chosenLoad?.number_of_loads;
        const currentLoadGifts = chosenNumberOfLoads
          ? currentGifts.filter(({minimum_load_to_apply}) => {
              return (
                minimum_load_to_apply !== null  &&
                minimum_load_to_apply <= chosenNumberOfLoads
              );
            })
          : [];
    
        const giftIds = currentLoadGifts.map(c => c.gift_variant?.id).filter(c => c)

        const giftLineItems = giftIds.map((id) => ({ id: Number(id), quantity: 1 }))

        const currentSettings = variantToSettings[chosenLoad.id] 
        const giftCodes = getGiftCodes(currentSettings)

        const donationCategory = getDonationCategory();
        const properties = donationCategory
          ? {
              _buy_one_give_ten_category: donationCategory,
            }
          : undefined;
        
        const mainLineItem = {
            id: parseInt(chosenLoad.id ?? ''),
            quantity: 1,
            properties,
          }

        await clearCart();
        await addLineItems([mainLineItem, ...giftLineItems])
        
        const url = `/checkout?discount=${giftCodes}`;
        await checkout(url);
   }

    const underBtnMsg = (productVariantId?: string) => {
        if(!productVariantId) return null

        const currentSettings = variantToSettings[productVariantId]

        if(!currentSettings) return null

        return <span className="text-xs uppercase text-zinc-500" >
            {currentSettings.subtitle}
        </span>
    }

    const hideGiftOverlay = (hideGift: boolean, minLoad: number | null) => {
        if(hideGift) 
            return (
                <>
                    <div className="absolute left-0 top-0 bg-white/60" >
                        <img className="h- w-auto " src={lockImg} alt="Lock" />
                    </div>
                    {minLoad && <span className="absolute bottom-2 left-1/2 w-full -translate-x-1/2 text-xs text-neutral-500" >{minLoad} LOADS</span>}
                </>
            )

        return null;
    }

    const getAboveBtnAlertColor = (chosenColor: string): string => {
        const colors: Record<string, string> = {
            'Dark Ocean': 'bg-dark-ocean',
            'Accent Red': 'bg-accent-red'
        }

        const color = colors[chosenColor]
        return color
    }

    const generateAboveBtnAlert = (productVariantId?: string) => {
        if(!productVariantId) {
            return null
        }
        const currentSettings = variantToSettings[productVariantId]

        if(!currentSettings) {
            return null
        }

        const { msg_text, msg_color } = currentSettings

        if(!msg_text || !msg_color ) {
            return null
        }

        const bgColor = getAboveBtnAlertColor(msg_color)

        return (
            <div className={`absolute -top-2 left-1/2 -translate-x-1/2 ${bgColor} rounded-md px-2 py-1 text-2xs leading-none text-crisp-white`} >
                {currentSettings.msg_text}
            </div>
        )
    }

    const chooseProductLoad = useCallback((o: VariantState) => {
        setChosenLoad(o)

        const _option = o.selectedOptions[1]
        selectOption({name: _option?.name, value: _option?.value});

        // Setting images for carousel
        const currentSetting = variantToSettings[o.id]
        const index = currentSetting.variants.map(v => v.id).indexOf(o?.id)
        const mainImage = currentSetting.hero_images[index]

        const images = [mainImage, ...currentSetting.carousel_images]
        carouselImagesSignal.setImages(images)
    }, [variantToSettings])

    const changingScent = (scent: string) => {
        setChosenScent(scent)
        const variants = otpSettingsArr?.map(o => o.variants)?.flat()
        const currentVariants = variants.filter(v => v.selectedOptions?.[0]?.value?.toLowerCase() === scent?.toLowerCase())
        const orderedVariants = [...currentVariants].sort((a,b) => (b?.number_of_loads ?? 0) - (a?.number_of_loads ?? 0))
        chooseProductLoad(orderedVariants[0])
    }

    useEffect(() => {
        const variants = otpSettingsArr?.map(o => o.variants)?.flat()
        const currentVariants = variants.filter(v => v.selectedOptions?.[0]?.value?.toLowerCase() === chosenScent?.toLowerCase())
        const orderedVariants = [...currentVariants].sort((a,b) => (b?.number_of_loads ?? 0) - (a?.number_of_loads ?? 0))
        setCurrentScentVariants(orderedVariants)
    }, [otpSettingsArr, chosenScent])

    useEffect(() => {
        if(chosenScent) {
            selectOption({name: optionName, value: chosenScent});
        }
    }, [chosenScent, optionName])

    useEffect(() => {
        const res: Record<string, OtpSettings> = {}
        otpSettingsArr.forEach((o) => {
            o?.variants?.forEach((v) => {
                res[v.id] = o
            })
        })
        setVariantToSettings(res)
    }, [otpSettingsArr])

    useEffect(() => {
        if(!chosenLoad && currentScentVariants?.length) {
            const _chosenLoad = currentScentVariants[0]
            chooseProductLoad(_chosenLoad)
        } 
    }, [currentScentVariants, chosenLoad, chooseProductLoad])

    useEffect(() => {
        if(chosenLoad?.id)  setCurrentGifts([])

        const gifts = variantToSettings[chosenLoad?.id ?? ""]?.gifts || []
        setCurrentGifts(gifts)

    }, [chosenLoad, variantToSettings])

    const currentSettings = chosenLoad ?  variantToSettings[chosenLoad.id] : null;
    const buttonLabel = currentSettings?.cta_button_label ?? "BUY IT NOW";

    return (
        <div className="flex flex-col gap-y-6" >
            <div className="flex flex-col gap-y-2" >
                <span className="text-xs font-bold uppercase text-blue-900/60" >Choose Your Scent</span>
                <div className="grid grid-cols-2 gap-2" >
                    {option?.values?.map((o, i) => {
                        const currentOption = optionSettings.get(o)
                        const currentBtnColor = scentToColor[o]
                        const selectedOptionBg = o === chosenScent ? scentToActiveColor[o] : ""

                        return(
                            <button key={`options-btn-${o}-${i}`} onClick={() => changingScent(o) } className={`btn ${currentBtnColor} ${selectedOptionBg} btn-outline btn-lg btn-square w-full p-2`} >
                                <div className="flex flex-row items-center justify-center gap-x-1.5 text-base text-deep-ocean-blue" >
                                    {currentOption?.value}
                                </div>
                            </button>
                        )
                    })}
                </div>
            </div>
            <div className="flex flex-col gap-y-3" >
                <div className="grid grid-cols-2 grid-rows-2 gap-3" >
                    {currentScentVariants.map((o) => {
                        const selectedOptionBg = o.id === chosenLoad?.id ? "bg-orange-50 border-slate-800" : " border-zinc-300"
                        
                        return (
                            <button 
                                onClick={() => {
                                   chooseProductLoad(o)
                                }}
                                key={`button-${o.id}`}
                                className={`btn ${selectedOptionBg} btn-outline btn-lg btn-square relative flex size-full flex-row items-center justify-center p-3`} 
                            >
                                <div className="flex flex-col items-center gap-1" >
                                    {generateAboveBtnAlert(o.id)}
                                    <span className="text-base font-bold text-dark-true-black" >{o.number_of_loads} Loads</span>
                                    {underBtnMsg(o.id)}
                                </div>
                            </button>
                        )
                    })}
                </div>

                <div className="flex flex-col items-center justify-center gap-y-3" >
                    <p className="text-xs" >
                        <strong>EXCLUSIVE SALE!</strong> FREE Gifts With Your First Order
                    </p>
                    <div className="flex w-full justify-around gap-x-2" >
                        {currentGifts.map((l, index) => {
                            const minLoads = l.minimum_load_to_apply
                            const hideGift = minLoads != null && minLoads > (chosenLoad?.number_of_loads ?? 0)
                            return (
                                <div key={`gift-${index}`} >
                                    <div className="relative rounded-md border-2 border-dashed border-blue-900 bg-blue-100 p-2" >
                                        <img 
                                            onClick={() => {
                                                if(l?.title && l?.image?.url){
                                                    setGiftInModal(l)
                                                    modalController.open()
                                                }
                                            }}
                                            className="h-14 w-auto cursor-pointer md:h-18"
                                            src={l.image.url}
                                            alt={l.title}
                                        />
                                        {hideGiftOverlay(hideGift, l.minimum_load_to_apply)}
                                    </div>
                                    <span className="text-xs" >Gift #{index+1}</span>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <button onClick={() => void addToCart()} className="btn btn-fresh-scent btn-square w-full text-center uppercase" >
                    {chosenLoad ? (
                        <>{buttonLabel}</>
                    ): (
                        <>BUY IT NOW</>
                    )}
                </button>
            </div>

            <Modal controller={modalController}>
              <div className="flex h-full flex-col items-center">
                <img className="h-56 w-auto" src={giftInModal?.image?.url} alt={giftInModal?.image?.alt ?? ""} />
                <h3>{giftInModal?.title}</h3>
                <p>{giftInModal?.description}</p>
             </div>
            </Modal>
        </div>
    )
}

export type SteppedProductSelectorProps = {
    settings: SteppedProductSelectorSettings;
    otpSettingsArr: OtpSettingsArr
};
